import React, { useEffect, useState } from "react"
import { ResponsiveContainer, LineChart, BarChart, Line, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceDot } from "recharts"
import CardFrame from "./CardFrame"
import { readable } from "./SegmentsCard"
import { formatValue, formatChange, formatBucket, formatPeriod, moneySymbol } from "../format"
import useDashboardTexts from "../useDashboardTexts"

const COLOR = "#3b6fd4"
const PREVIOUS = "#9aa7bd"
// the lines of a split chart, one colour each
const PALETTE = ["#3b6fd4", "#e8833a", "#2fa36b", "#c2477a", "#8a63d2", "#d4a72c", "#3aa6b9", "#7d8597"]

/**
 * The bucket "now" falls in, in the scope's time zone, keyed as the server
 * keys them: "YYYY-MM-DD HH:00" (hour), the date (day), its Monday (week),
 * "YYYY-MM" (month).
 */
function nowKey(granularity, timeZone) {
  let local
  try {
    local = new Intl.DateTimeFormat("sv-SE", { timeZone: timeZone || undefined, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", hourCycle: "h23" }).format(new Date())
  } catch {
    return null
  }
  const date = local.slice(0, 10)
  if (granularity === "hour") return `${date} ${local.slice(11, 13)}:00`
  if (granularity === "day") return date
  if (granularity === "month") return date.slice(0, 7)
  const ms = Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)))
  const weekday = (new Date(ms).getUTCDay() + 6) % 7
  return new Date(ms - weekday * 86400000).toISOString().slice(0, 10)
}

/** "Now" on the line: a dot, softly pulsing (Tailwind's animate-ping, scaled about its own centre). */
const NowDot = ({ cx, cy }) =>
  // recharts hands NaN when the point is not on the axis (no data yet, a label it could not place)
  !Number.isFinite(cx) || !Number.isFinite(cy) ? null : (
    <g data-testid="chart-now">
      <circle cx={cx} cy={cy} r={7} fill={COLOR} opacity={0.35} className="animate-ping" style={{ transformBox: "fill-box", transformOrigin: "center" }} />
      <circle cx={cx} cy={cy} r={4} fill={COLOR} stroke="#fff" strokeWidth={2} />
    </g>
  )

const LEGEND_SHOWN = 4

/** Below 768px: the chart takes the card's full width, with a slimmer axis. */
function usePhone() {
  const [phone, setPhone] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)")
    const update = () => setPhone(mq.matches)
    update()
    mq.addEventListener("change", update)
    return () => mq.removeEventListener("change", update)
  }, [])
  return phone
}

/** An axis figure in few characters: $1.2k, $120, 3.4k, 12%. */
const shortTick = (value, unit) => {
  const n = unit === "money" ? (Number(value) || 0) / 100 : Number(value) || 0
  const abs = Math.abs(n)
  const text = abs >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : abs >= 1e3 ? `${(n / 1e3).toFixed(abs >= 1e4 ? 0 : 1)}k` : `${Math.round(n * (abs < 10 && unit !== "count" ? 10 : 1)) / (abs < 10 && unit !== "count" ? 10 : 1)}`
  return unit === "money" ? `${moneySymbol()}${text}` : unit === "percent" ? `${text}%` : text
}

/** The lines with something at the point, biggest first — the empty ones would only fill the box. */
function SplitTooltip({ active, payload, unit }) {
  const t = useDashboardTexts()
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload
  const items = payload.filter((item) => item.value).sort((a, b) => b.value - a.value)
  return (
    <div className="rounded-lg border border-admin_bordered bg-admin_paper px-3 py-2 shadow-md text-xs max-w-[320px]">
      <div className="font-semibold text-admin_text mb-1">{row?.tip || row?.label}</div>
      {items.length ? (
        items.map((item) => (
          <div key={item.dataKey} className="flex items-center gap-2 min-w-0">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: item.color }} />
            <span className="truncate flex-1" style={{ color: item.color }}>{item.name}</span>
            <span className="tabular-nums text-admin_text">{formatValue(item.value, unit)}</span>
          </div>
        ))
      ) : (
        <div className="text-admin_text_secondary">{t`Nothing here`}</div>
      )}
    </div>
  )
}

/**
 * A split chart's legend, kept to a line: names cut short (the whole on
 * hover), the first few with the rest behind "+N more", and a click on one
 * hides or shows its line.
 */
function SplitLegend({ items, hidden, onToggle }) {
  const t = useDashboardTexts()
  const [all, setAll] = useState(false)
  const shown = all ? items : items.slice(0, LEGEND_SHOWN)
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 mt-2 text-xs" data-testid="split-legend">
      {shown.map((item) => {
        const off = hidden.has(item.key)
        return (
          <button
            key={item.key}
            type="button"
            onClick={() => onToggle(item.key)}
            title={`${item.name}${off ? ` (${t`hidden`})` : ""}`}
            className={`inline-flex items-center gap-1.5 max-w-[180px] ${off ? "opacity-40" : ""}`}
          >
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: item.color }} />
            <span className="truncate" style={{ color: item.color }}>{item.name}</span>
          </button>
        )
      })}
      {items.length > LEGEND_SHOWN && (
        <button type="button" onClick={() => setAll(!all)} className="font-semibold text-admin_text_secondary hover:text-admin_primary">
          {all ? t`Less` : t("+%s more", items.length - LEGEND_SHOWN)}
        </button>
      )}
    </div>
  )
}

/**
 * A metric over the period, bucket by bucket, the compared period drawn
 * beside it. A period that runs past now (Today, by the hour) is drawn up to
 * now, with a dot there; the compared period still runs the whole way.
 */
export default function ChartCard({ card, def, data, loading, period, comparison: periodBefore, timeZone, dimensions, onEdit }) {
  const t = useDashboardTexts()
  const phone = usePhone()
  // split by a dimension: a line per value, and no earlier period beside them
  const split = !!data?.split
  const comparison = split ? null : periodBefore
  const series = data?.series?.[0]
  const splitLabel = split ? t(dimensions?.find((d) => d.id === data.split)?.label) : ""
  // the split lines turned off in the legend
  const [hidden, setHidden] = useState(() => new Set())
  const toggleLine = (key) =>
    setHidden((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  const legendItems = split ? data.series.map((line, j) => ({ key: line.key, name: readable(data.split, line.label), color: PALETTE[j % PALETTE.length] })) : []
  const granularity = data?.granularity
  const keys = series?.keys || []
  const nowAt = keys.indexOf(nowKey(granularity, timeZone))
  const rows = keys.map((key, i) => ({
    // a week or a month reads as the days of it the period holds
    label: formatBucket(key, granularity, period, { last: i === keys.length - 1 }),
    tip: formatBucket(key, granularity, period, { full: true }),
    // the line stops at now: what is still to come is not drawn
    current: nowAt < 0 || i <= nowAt ? series.values[i] : null,
    ...(split ? Object.fromEntries(data.series.map((line, j) => [`s${j}`, nowAt < 0 || i <= nowAt ? line.values[i] : null])) : {}),
    previous: series.previousValues?.[i] ?? null,
    previousLabel: series.previousKeys?.[i] ? formatBucket(series.previousKeys[i], granularity, comparison, { full: true }) : "",
  }))
  const now = nowAt >= 0 && !split ? rows[nowAt] : null
  const change = series && series.previousTotal !== undefined ? formatChange(series.total, series.previousTotal) : null
  const Chart = card.chart === "bar" ? BarChart : LineChart
  const unit = def?.unit
  const tick = (v) => formatValue(v, unit, { compact: true })

  return (
    <CardFrame
      title={(card.title && t(card.title)) || t(def?.label) || card.metric}
      subtitle={granularity ? `${t("by %s", t(granularity))}${splitLabel ? ` · ${t("per %s", splitLabel.toLowerCase())}` : ""}` : ""}
      onEdit={onEdit}
      testId="dashboard-chart"
    >
      {data?.error ? (
        <span className="text-sm text-admin_error">{t`Could not load`}</span>
      ) : (
        <>
          <div className="flex items-baseline gap-3 mb-2">
            <span className="text-[24px] font-semibold text-admin_text tabular-nums">{split ? formatValue(data.total, unit) : series ? formatValue(series.total, unit) : "…"}</span>
            {change && <span className={`text-sm font-semibold ${change.className}`}>{change.text}</span>}
          </div>
          {/* on a phone the chart runs to the card's edges: more of the period in sight */}
          <div className={`h-[240px] -mx-3 md:mx-0 ${loading ? "opacity-40" : ""}`}>
            <ResponsiveContainer width="100%" height="100%">
              <Chart data={rows} margin={phone ? { top: 4, right: 4, bottom: 0, left: 0 } : { top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: phone ? 10 : 11 }} interval="preserveStartEnd" minTickGap={phone ? 8 : 16} />
                <YAxis tickFormatter={phone ? (v) => shortTick(v, unit) : tick} tick={{ fontSize: phone ? 10 : 11 }} width={phone ? 40 : 64} />
                <Tooltip
                  formatter={(value, name) => [formatValue(value, unit), name]}
                  // a split chart's tooltip names only the lines with something at that point, biggest first
                  {...(split ? { content: (props) => <SplitTooltip {...props} unit={unit} /> } : {})}
                  labelFormatter={(label, payload) => {
                    const row = payload?.[0]?.payload
                    const text = row?.tip || label
                    return row?.previousLabel ? `${text} (vs ${row.previousLabel})` : text
                  }}
                />
                {comparison && !split && <Legend wrapperStyle={{ fontSize: 12 }} />}
                {split ? (
                  data.series.map((line, j) => {
                    const name = readable(data.split, line.label)
                    const off = hidden.has(line.key)
                    return card.chart === "bar" ? (
                      <Bar key={line.key} dataKey={`s${j}`} name={name} fill={PALETTE[j % PALETTE.length]} stackId="split" hide={off} />
                    ) : (
                      <Line key={line.key} type="monotone" dataKey={`s${j}`} name={name} stroke={PALETTE[j % PALETTE.length]} dot={false} strokeWidth={2} hide={off} />
                    )
                  })
                ) : card.chart === "bar" ? (
                  <>
                    {comparison && <Bar dataKey="previous" name={formatPeriod(comparison)} fill={PREVIOUS} />}
                    <Bar dataKey="current" name={formatPeriod(period)} fill={COLOR} />
                  </>
                ) : (
                  <>
                    {comparison && <Line type="monotone" dataKey="previous" name={formatPeriod(comparison)} stroke={PREVIOUS} strokeDasharray="5 4" dot={false} strokeWidth={2} />}
                    <Line type="monotone" dataKey="current" name={formatPeriod(period)} stroke={COLOR} dot={false} strokeWidth={2} />
                    {now && Number.isFinite(now.current) && <ReferenceDot x={now.label} y={now.current} shape={NowDot} ifOverflow="visible" />}
                  </>
                )}
              </Chart>
            </ResponsiveContainer>
          </div>
          {split && <SplitLegend items={legendItems} hidden={hidden} onToggle={toggleLine} />}
        </>
      )}
    </CardFrame>
  )
}
